import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bot, Send, Sparkles, X, Minimize2 } from "lucide-react";
import { toast } from "sonner";

/*
 * ============================================================
 * NEXA AI — SUPABASE EDGE FUNCTION
 * ============================================================
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const API = `${SUPABASE_URL}/functions/v1/nexa-chat`;

const QUICK_PROMPTS = [
  "How can I increase my revenue?",
  "Analyze my competitors",
  "Create a marketing strategy",
  "How can AI reduce my costs?",
];

/*
 * ============================================================
 * RESPONSE EXTRACTION
 * ============================================================
 */

function getReply(data) {
  if (!data) {
    return "NEXA AI returned an empty response.";
  }

  if (typeof data === "string") {
    return data;
  }

  return (
    data.reply ||
    data.response ||
    data.message ||
    data.answer ||
    data.content ||
    data.result ||
    data.analysis ||
    data.output ||
    data.text ||
    data
  );
}

function normalizeResponse(value) {
  if (value === null || value === undefined) {
    return "I couldn't generate a response. Please try again.";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "string"
          ? item
          : JSON.stringify(item, null, 2)
      )
      .join("\n");
  }

  if (typeof value === "object") {
    return (
      value.reply ||
      value.response ||
      value.message ||
      value.answer ||
      value.content ||
      JSON.stringify(value, null, 2)
    );
  }

  return String(value);
}

/*
 * ============================================================
 * MARKDOWN-LIKE MESSAGE RENDERER
 *
 * No additional package required.
 * Supports:
 * - headings
 * - bold
 * - italic
 * - bullets
 * - numbered lists
 * - paragraphs
 * - blockquotes
 * - links
 * ============================================================
 */

function renderInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;

  const regex =
    /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*(.*?)\*\*|__(.*?)__|\*(.*?)\*|_(.*?)_)/;

  while (remaining.length > 0) {
    const match = remaining.match(regex);

    if (!match) {
      parts.push(
        <React.Fragment key={key++}>{remaining}</React.Fragment>
      );
      break;
    }

    const index = match.index;

    if (index > 0) {
      parts.push(
        <React.Fragment key={key++}>
          {remaining.slice(0, index)}
        </React.Fragment>
      );
    }

    if (match[1]?.startsWith("[")) {
      parts.push(
        <a
          key={key++}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-300 underline underline-offset-2 hover:text-cyan-200"
        >
          {match[2]}
        </a>
      );
    } else if (match[3]) {
      parts.push(
        <strong
          key={key++}
          className="font-semibold text-white"
        >
          {match[3]}
        </strong>
      );
    } else if (match[4]) {
      parts.push(
        <strong
          key={key++}
          className="font-semibold text-white"
        >
          {match[4]}
        </strong>
      );
    } else if (match[5]) {
      parts.push(
        <em key={key++} className="italic text-white/90">
          {match[5]}
        </em>
      );
    } else if (match[6]) {
      parts.push(
        <em key={key++} className="italic text-white/90">
          {match[6]}
        </em>
      );
    }

    remaining = remaining.slice(index + match[0].length);
  }

  return parts;
}

function MessageContent({ text }) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");

  const elements = [];
  let listItems = [];
  let listType = null;

  const flushList = () => {
    if (!listItems.length) return;

    const items = [...listItems];

    elements.push(
      listType === "ordered" ? (
        <ol
          key={`ol-${elements.length}`}
          className="my-3 ml-5 list-decimal space-y-2 text-white/80"
        >
          {items.map((item, index) => (
            <li key={index} className="pl-1">
              {renderInline(item)}
            </li>
          ))}
        </ol>
      ) : (
        <ul
          key={`ul-${elements.length}`}
          className="my-3 ml-5 list-disc space-y-2 text-white/80"
        >
          {items.map((item, index) => (
            <li key={index} className="pl-1">
              {renderInline(item)}
            </li>
          ))}
        </ul>
      )
    );

    listItems = [];
    listType = null;
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    // Empty line
    if (!trimmed) {
      flushList();
      elements.push(
        <div
          key={`space-${index}`}
          className="h-2"
        />
      );
      return;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushList();

      elements.push(
        <div
          key={`hr-${index}`}
          className="my-4 h-px bg-white/10"
        />
      );

      return;
    }

    // H1
    if (/^# /.test(trimmed)) {
      flushList();

      elements.push(
        <h2
          key={`h1-${index}`}
          className="mt-4 mb-2 text-lg font-bold text-white"
        >
          {renderInline(trimmed.replace(/^# /, ""))}
        </h2>
      );

      return;
    }

    // H2
    if (/^## /.test(trimmed)) {
      flushList();

      elements.push(
        <h3
          key={`h2-${index}`}
          className="mt-4 mb-2 text-base font-bold text-cyan-200"
        >
          {renderInline(trimmed.replace(/^## /, ""))}
        </h3>
      );

      return;
    }

    // H3
    if (/^### /.test(trimmed)) {
      flushList();

      elements.push(
        <h4
          key={`h3-${index}`}
          className="mt-4 mb-2 text-sm font-semibold text-fuchsia-200"
        >
          {renderInline(trimmed.replace(/^### /, ""))}
        </h4>
      );

      return;
    }

    // H4
    if (/^#### /.test(trimmed)) {
      flushList();

      elements.push(
        <h5
          key={`h4-${index}`}
          className="mt-3 mb-1 text-sm font-semibold text-white"
        >
          {renderInline(trimmed.replace(/^#### /, ""))}
        </h5>
      );

      return;
    }

    // Bullet list
    const bulletMatch = trimmed.match(/^[-*•]\s+(.*)$/);

    if (bulletMatch) {
      if (listType && listType !== "unordered") {
        flushList();
      }

      listType = "unordered";
      listItems.push(bulletMatch[1]);
      return;
    }

    // Numbered list
    const numberMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);

    if (numberMatch) {
      if (listType && listType !== "ordered") {
        flushList();
      }

      listType = "ordered";
      listItems.push(numberMatch[1]);
      return;
    }

    // Blockquote
    if (trimmed.startsWith(">")) {
      flushList();

      elements.push(
        <blockquote
          key={`quote-${index}`}
          className="my-3 border-l-2 border-cyan-400/60 pl-3 text-white/60 italic"
        >
          {renderInline(trimmed.replace(/^>\s?/, ""))}
        </blockquote>
      );

      return;
    }

    // Regular paragraph
    flushList();

    elements.push(
      <p
        key={`p-${index}`}
        className="my-2 text-white/80"
      >
        {renderInline(trimmed)}
      </p>
    );
  });

  flushList();

  return <div className="space-y-1">{elements}</div>;
}

/*
 * ============================================================
 * NEXA BOT
 * ============================================================
 */

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Hello! I'm **NEXA AI**. 👋\n\nI can help you with **revenue growth, marketing, competitor analysis, automation and business strategy.**\n\nHow can I help your business today?",
    },
  ]);

  /*
   * Automatically scroll to newest message
   */
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages, loading, open]);

  /*
   * Focus input when chat opens
   */
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  /*
   * ==========================================================
   * SEND MESSAGE
   * ==========================================================
   */

  const sendMessage = async (message = input) => {
    const text = String(message || "").trim();

    if (!text || loading) {
      return;
    }

    /*
     * Check environment variables before making request
     */
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      console.error("NEXA CONFIG ERROR:", {
        SUPABASE_URL: Boolean(SUPABASE_URL),
        SUPABASE_ANON_KEY: Boolean(SUPABASE_ANON_KEY),
      });

      toast.error("NEXA AI is not configured correctly.");

      setMessages((previous) => [
        ...previous,
        {
          role: "assistant",
          text:
            "⚠️ **NEXA AI configuration error.**\n\nThe Supabase connection settings are missing from the frontend environment.",
        },
      ]);

      return;
    }

    /*
     * Add user message immediately
     */
    setMessages((previous) => [
      ...previous,
      {
        role: "user",
        text,
      },
    ]);

    setInput("");
    setLoading(true);

    try {
      console.log("NEXA AI REQUEST:", text);
      console.log("NEXA API:", API);

      const response = await axios.post(
        API,
        {
          message: text,
        },
        {
          timeout: 60000,
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
        }
      );

      console.log("NEXA AI RESPONSE:", response.data);

      const reply = normalizeResponse(
        getReply(response.data)
      );

      setMessages((previous) => [
        ...previous,
        {
          role: "assistant",
          text: reply,
        },
      ]);
    } catch (error) {
      console.error("NEXA AI ERROR:", error);

      let errorMessage =
        "⚠️ **NEXA AI couldn't connect right now.**\n\nPlease try again in a moment.";

      if (error.response) {
        console.error(
          "NEXA API STATUS:",
          error.response.status
        );

        console.error(
          "NEXA API ERROR DATA:",
          error.response.data
        );

        if (error.response.status === 401) {
          errorMessage =
            "⚠️ **NEXA authentication failed.**\n\nPlease check the Supabase API configuration.";
        } else if (error.response.status === 404) {
          errorMessage =
            "⚠️ **NEXA endpoint not found.**\n\nPlease verify that the `nexa-chat` Supabase Edge Function is deployed.";
        } else if (error.response.status >= 500) {
          errorMessage =
            "⚠️ **NEXA server error.**\n\nThe AI service returned an internal error. Please try again.";
        }
      } else if (error.request) {
        errorMessage =
          "⚠️ **NEXA AI couldn't reach the server.**\n\nPlease check your internet connection and try again.";
      }

      setMessages((previous) => [
        ...previous,
        {
          role: "assistant",
          text: errorMessage,
        },
      ]);

      toast.error("NEXA AI connection failed.");
    } finally {
      setLoading(false);
    }
  };

  /*
   * ==========================================================
   * KEYBOARD HANDLING
   * ==========================================================
   */

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  /*
   * ==========================================================
   * RENDER
   * ==========================================================
   */

  return (
    <>
      {/* =====================================================
          FLOATING NEXA BUTTON
      ====================================================== */}

      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="nexa-open-button"
          aria-label="Open NEXA AI"
          className="
            fixed
            right-5
            bottom-6
            z-[9999]
            w-16
            h-16
            rounded-full
            flex
            items-center
            justify-center
            bg-gradient-to-br
            from-fuchsia-500
            via-purple-500
            to-cyan-400
            shadow-[0_0_35px_rgba(217,70,239,0.55)]
            hover:scale-110
            active:scale-95
            transition-all
            duration-300
          "
        >
          <Bot
            size={30}
            strokeWidth={2.2}
            className="text-white"
          />

          <span
            className="
              absolute
              top-0
              right-0
              w-4
              h-4
              rounded-full
              bg-cyan-400
              border-2
              border-[#050816]
            "
          />

          <span
            className="
              absolute
              right-0
              -bottom-8
              px-3
              py-1
              rounded-full
              bg-[#070B1D]
              border
              border-cyan-400/30
              text-[9px]
              font-mono
              tracking-[0.22em]
              text-cyan-300
              whitespace-nowrap
            "
          >
            NEXA BOT
          </span>
        </button>
      )}

      {/* =====================================================
          CHAT WINDOW
      ====================================================== */}

      {open && (
        <div
          data-testid="nexa-chat-window"
          className="
            fixed
            right-4
            bottom-4
            sm:right-6
            sm:bottom-6
            z-[9999]
            w-[calc(100vw-32px)]
            sm:w-[440px]
            h-[680px]
            max-h-[calc(100vh-32px)]
            sm:max-h-[88vh]
            flex
            flex-col
            overflow-hidden
            rounded-3xl
            bg-[#050816]
            border
            border-cyan-400/20
            shadow-[0_0_70px_rgba(0,240,255,0.18)]
          "
        >
          {/* =================================================
              HEADER
          ================================================== */}

          <div
            className="
              flex
              items-center
              justify-between
              px-5
              py-4
              border-b
              border-white/10
              bg-gradient-to-r
              from-fuchsia-500/10
              via-purple-500/10
              to-cyan-500/10
              flex-shrink-0
            "
          >
            <div className="flex items-center gap-3">
              <div
                className="
                  w-11
                  h-11
                  rounded-2xl
                  flex
                  items-center
                  justify-center
                  bg-gradient-to-br
                  from-fuchsia-500
                  to-cyan-400
                  shadow-[0_0_25px_rgba(217,70,239,0.35)]
                "
              >
                <Bot
                  size={23}
                  className="text-white"
                />
              </div>

              <div>
                <div
                  className="
                    font-[Outfit]
                    font-bold
                    text-white
                    text-lg
                  "
                >
                  NEXA AI
                </div>

                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                    font-mono
                    text-[9px]
                    tracking-[0.2em]
                    text-cyan-300
                  "
                >
                  <span
                    className="
                      w-1.5
                      h-1.5
                      rounded-full
                      bg-green-400
                      shadow-[0_0_8px_rgba(74,222,128,0.8)]
                    "
                  />

                  ONLINE
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Minimize NEXA AI"
                className="
                  w-9
                  h-9
                  rounded-xl
                  flex
                  items-center
                  justify-center
                  text-white/50
                  hover:text-white
                  hover:bg-white/10
                  transition
                "
              >
                <Minimize2 size={17} />
              </button>

              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close NEXA AI"
                className="
                  w-9
                  h-9
                  rounded-xl
                  flex
                  items-center
                  justify-center
                  text-white/50
                  hover:text-white
                  hover:bg-white/10
                  transition
                "
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* =================================================
              MESSAGES
          ================================================== */}

          <div
            className="
              flex-1
              overflow-y-auto
              p-4
              space-y-4
              bg-[#030711]
              scrollbar-thin
              scrollbar-thumb-white/10
              scrollbar-track-transparent
            "
          >
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`flex ${
                  message.role === "user"
                    ? "justify-end"
                    : "justify-start"
                }`}
              >
                <div
                  className={`
                    max-w-[90%]
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                    leading-6
                    break-words
                    ${
                      message.role === "user"
                        ? `
                          bg-gradient-to-r
                          from-fuchsia-500
                          to-purple-500
                          text-white
                          rounded-br-md
                          shadow-[0_8px_25px_rgba(168,85,247,0.18)]
                        `
                        : `
                          bg-white/[0.06]
                          border
                          border-white/10
                          text-white/80
                          rounded-bl-md
                        `
                    }
                  `}
                >
                  {message.role === "assistant" && (
                    <div
                      className="
                        flex
                        items-center
                        gap-1.5
                        mb-2
                        text-[9px]
                        font-mono
                        tracking-[0.2em]
                        text-cyan-300/70
                      "
                    >
                      <Sparkles size={11} />
                      NEXA AI
                    </div>
                  )}

                  <MessageContent text={message.text} />
                </div>
              </div>
            ))}

            {/* Loading */}
            {loading && (
              <div className="flex justify-start">
                <div
                  className="
                    rounded-2xl
                    rounded-bl-md
                    px-4
                    py-3
                    bg-white/[0.06]
                    border
                    border-white/10
                  "
                >
                  <div
                    className="
                      flex
                      items-center
                      gap-2
                      text-xs
                      text-white/50
                    "
                  >
                    <span className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" />
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce"
                        style={{ animationDelay: "120ms" }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce"
                        style={{ animationDelay: "240ms" }}
                      />
                    </span>

                    NEXA AI is analyzing...
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* =================================================
              QUICK PROMPTS
          ================================================== */}

          <div
            className="
              px-4
              pt-3
              border-t
              border-white/10
              bg-[#050816]
              flex-shrink-0
            "
          >
            <div
              className="
                flex
                gap-2
                overflow-x-auto
                pb-3
                scrollbar-hide
              "
            >
              {QUICK_PROMPTS.map((prompt) => (
                <button
                  type="button"
                  key={prompt}
                  disabled={loading}
                  onClick={() => sendMessage(prompt)}
                  className="
                    flex-shrink-0
                    px-3
                    py-2
                    rounded-full
                    bg-white/[0.04]
                    border
                    border-white/10
                    text-[10px]
                    text-white/60
                    hover:text-cyan-300
                    hover:border-cyan-400/30
                    disabled:opacity-30
                    transition-all
                  "
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          {/* =================================================
              INPUT
          ================================================== */}

          <div
            className="
              p-4
              bg-[#050816]
              flex-shrink-0
            "
          >
            <div
              className="
                flex
                items-end
                gap-2
                rounded-2xl
                p-2
                bg-white/[0.05]
                border
                border-white/10
                focus-within:border-cyan-400/40
                transition
              "
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={(event) =>
                  setInput(event.target.value)
                }
                onKeyDown={handleKeyDown}
                disabled={loading}
                rows={1}
                placeholder="Ask NEXA AI..."
                className="
                  flex-1
                  min-w-0
                  max-h-28
                  resize-none
                  bg-transparent
                  px-3
                  py-2
                  text-sm
                  text-white
                  placeholder:text-white/30
                  outline-none
                  leading-5
                "
              />

              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                aria-label="Send message"
                className="
                  flex-shrink-0
                  w-10
                  h-10
                  rounded-xl
                  flex
                  items-center
                  justify-center
                  bg-gradient-to-r
                  from-fuchsia-500
                  to-cyan-400
                  text-white
                  disabled:opacity-30
                  disabled:cursor-not-allowed
                  hover:scale-105
                  active:scale-95
                  transition-all
                "
              >
                <Send size={17} />
              </button>
            </div>

            <div
              className="
                text-center
                mt-2
                text-[9px]
                font-mono
                tracking-wider
                text-white/25
              "
            >
              ENTER TO SEND · SHIFT + ENTER FOR NEW LINE
            </div>

            <div
              className="
                text-center
                mt-1
                text-[8px]
                font-mono
                tracking-wider
                text-white/20
              "
            >
              POWERED BY MUNNEXA INTELLIGENCE
            </div>
          </div>
        </div>
      )}
    </>
  );
}
